'use client';

import { useEffect } from 'react';
import { Provider, useSelector } from 'react-redux';
import { getCookie } from 'cookies-next/client';
import { store } from '@/hotdesk-ui/store/store';
import type { RootState } from '@/hotdesk-ui/store/store';
import { StorageKeys } from '@/hotdesk-ui/enum/storage-keys';
import { storeSet } from '@/hotdesk-ui/helpers/utils';
const AUTH_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days in seconds

/**
 * Redux persists the auth token (survives a browser restart), but the
 * `accessToken` cookie the server components read used to be session-only. If a
 * restart drops the cookie while redux still holds the token, /my-account,
 * /my-bookings & /favorites fail to the landing page. Re-writing the cookie from the
 * rehydrated token self-heals those sessions without forcing a re-login. New
 * logins already set a persistent cookie (see auth.slice).
 */
function AuthCookieSync() {
  const token = useSelector((s: RootState) => s.auth.token);
  const refresh = useSelector((s: RootState) => s.auth.refresh);
  useEffect(() => {
    if (token && !getCookie(StorageKeys.ACCESS_TOKEN)) {
      storeSet(StorageKeys.ACCESS_TOKEN, token, {
        maxAge: AUTH_COOKIE_MAX_AGE,
        path: '/'
      });
    }
    if (refresh && !getCookie(StorageKeys.REFRESH_TOKEN)) {
      storeSet(StorageKeys.REFRESH_TOKEN, refresh, {
        maxAge: AUTH_COOKIE_MAX_AGE,
        path: '/'
      });
    }
  }, [token, refresh]);
  return null;
}
export function ReduxProvider({
  children
}: {
  children: React.ReactNode;
}) {
  return <Provider store={store} data-sentry-element="Provider" data-sentry-component="ReduxProvider" data-sentry-source-file="redux.provider.tsx">
            <AuthCookieSync data-sentry-element="AuthCookieSync" data-sentry-source-file="redux.provider.tsx" />
            {children}
        </Provider>;
}